import { Command } from "commander";
import { readFileSync, existsSync } from "node:fs";
import { resolve } from "node:path";
import chalk from "chalk";
import Table from "cli-table3";
import { loadData, getModelPricing } from "@fathom/core";
import {
  calculateProjectVelocity,
  generateBenchmarks,
  type TrackedFeatureData,
} from "@fathom/velocity";
import { syncVelocity, logConvexStatus } from "../convex-client.js";
import { getProjectName } from "../project-name.js";
import { fatalError } from "../ux/errors.js";

interface TrackedSession {
  sessionId: string;
  featureId?: string;
  status: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  model: string;
  sessionStart: number;
  sessionEnd?: number;
  activeMinutes?: number;
  confidence: number;
}

interface RegistryFeature {
  id: string;
  name: string;
  estimated_tokens: number;
  status: string;
}

export const velocityCommand = new Command("velocity")
  .description("Show velocity metrics and benchmarks")
  .option("-p, --project <name>", "Project name")
  .option("--registry <path>", "Path to registry.json")
  .option("--benchmarks", "Show benchmarks by complexity + category")
  .option("--json", "Output machine-readable JSON")
  .action(
    async (options: {
      project?: string;
      registry?: string;
      benchmarks?: boolean;
      json?: boolean;
    }) => {
      const registryPath =
        options.registry ??
        resolve(process.cwd(), ".fathom", "registry.json");
      const summaryPath = resolve(
        process.cwd(),
        ".fathom",
        "tracking",
        "summary.json",
      );

      if (!existsSync(registryPath) || !existsSync(summaryPath)) {
        console.log(
          chalk.yellow(
            "Need both registry and tracking data. Run `fathom analyze` then `fathom track` first.",
          ),
        );
        return;
      }

      let registry: { project?: string; features: RegistryFeature[] };
      try {
        registry = JSON.parse(readFileSync(registryPath, "utf-8"));
      } catch {
        fatalError("Invalid registry", "Failed to parse " + registryPath, "Delete and re-run fathom analyze");
      }
      let summary: { sessions: TrackedSession[] };
      try {
        summary = JSON.parse(readFileSync(summaryPath, "utf-8"));
      } catch {
        fatalError("Invalid tracking data", "Failed to parse " + summaryPath, "Delete and re-run fathom track --backfill");
      }
      const projectName = options.project ?? registry.project ?? getProjectName();
      const data = loadData();

      // Build tracked feature data
      const featureDataMap = new Map<string, TrackedFeatureData>();

      for (const feature of registry.features as RegistryFeature[]) {
        featureDataMap.set(feature.id, {
          featureId: feature.id,
          featureName: feature.name,
          category: "general", // from registry, simplified
          complexity: "M", // would come from estimate data
          estimatedTokens: feature.estimated_tokens,
          status: feature.status,
          sessions: [],
        });
      }

      for (const session of summary.sessions as TrackedSession[]) {
        if (!session.featureId || session.status === "untagged") continue;

        const featureData = featureDataMap.get(session.featureId);
        if (!featureData) continue;

        const pricing = getModelPricing(data.models, session.model);
        const cost = pricing
          ? (session.inputTokens / 1_000_000) * pricing.input_per_mtok +
            (session.outputTokens / 1_000_000) * pricing.output_per_mtok +
            (session.cacheReadTokens / 1_000_000) * pricing.cache_read_per_mtok
          : 0;

        featureData.sessions.push({
          sessionId: session.sessionId,
          inputTokens: session.inputTokens,
          outputTokens: session.outputTokens,
          cacheReadTokens: session.cacheReadTokens,
          model: session.model,
          cost,
          sessionStart: session.sessionStart,
          sessionEnd: session.sessionEnd,
          activeMinutes: session.activeMinutes,
          confidence: session.confidence,
        });
      }

      const features = Array.from(featureDataMap.values());
      const projectVelocity = calculateProjectVelocity(projectName, features);

      // JSON output mode
      if (options.json) {
        const totalTokens = features.reduce((sum, f) => sum + f.sessions.reduce((s, sess) => s + sess.inputTokens + sess.outputTokens, 0), 0);
        const avgTokensPerSession = projectVelocity.totalSessions > 0 ? Math.round(totalTokens / projectVelocity.totalSessions) : 0;
        console.log(JSON.stringify({
          project: projectName,
          features: features.map(f => ({
            id: f.featureId,
            name: f.featureName,
            sessions: f.sessions.length,
            estimatedTokens: f.estimatedTokens,
            actualTokens: f.sessions.reduce((s, sess) => s + sess.inputTokens + sess.outputTokens, 0),
          })),
          featuresPerWeek: projectVelocity.featuresPerWeek,
          avgCostPerFeature: projectVelocity.avgCostPerFeature,
          avgSessionsPerFeature: projectVelocity.avgSessionsPerFeature,
          averageTokensPerSession: avgTokensPerSession,
        }, null, 2));
        return;
      }

      // Display overview
      console.log(chalk.bold(`\nFathom — Velocity: ${projectName}`));
      console.log(chalk.dim("─".repeat(65)));

      console.log(`  Features: ${projectVelocity.featuresCompleted} done, ${projectVelocity.featuresInProgress} active, ${projectVelocity.featuresTotal} total`);
      console.log(`  Throughput: ${projectVelocity.featuresPerWeek.toFixed(1)} features/week`);
      console.log(`  Avg cost/feature: $${projectVelocity.avgCostPerFeature.toFixed(2)}`);
      console.log(`  Avg sessions/feature: ${projectVelocity.avgSessionsPerFeature.toFixed(1)}`);
      console.log(`  Avg wall-clock: ${projectVelocity.avgWallClockDays.toFixed(1)} days`);
      console.log(`  Avg active time: ${projectVelocity.avgActiveHours.toFixed(1)} hours`);
      console.log(`  Estimate accuracy: ${(projectVelocity.overallAccuracy * 100).toFixed(0)}%`);
      console.log(`  Total: ${projectVelocity.totalTokens.toLocaleString()} tokens, $${projectVelocity.totalCost.toFixed(2)}`);

      // Feature table
      console.log(chalk.dim("\n─".repeat(65)));

      const table = new Table({
        head: [
          chalk.white("Feature"),
          chalk.white("Sessions"),
          chalk.white("Tokens"),
          chalk.white("Cost"),
          chalk.white("Active"),
          chalk.white("Accuracy"),
        ],
        colAligns: ["left", "center", "right", "right", "right", "center"],
      });

      for (const f of projectVelocity.features) {
        if (f.sessionCount === 0) continue;

        const accuracyLabel =
          f.estimateAccuracy > 0
            ? f.estimateAccuracy > 0.8 && f.estimateAccuracy < 1.2
              ? chalk.green(`${(f.estimateAccuracy * 100).toFixed(0)}%`)
              : chalk.yellow(`${(f.estimateAccuracy * 100).toFixed(0)}%`)
            : chalk.dim("—");

        table.push([
          f.featureName,
          f.sessionCount.toString(),
          f.totalTokens.toLocaleString(),
          `$${f.totalCost.toFixed(2)}`,
          `${f.activeHours.toFixed(1)}h`,
          accuracyLabel,
        ]);
      }

      console.log(table.toString());

      // Benchmarks
      if (options.benchmarks) {
        const benchmarks = generateBenchmarks(projectVelocity.features);
        if (benchmarks.length > 0) {
          console.log(chalk.bold("\nBenchmarks"));
          console.log(chalk.dim("─".repeat(65)));

          const benchTable = new Table({
            head: [
              chalk.white("Type"),
              chalk.white("Data Pts"),
              chalk.white("Avg Tokens"),
              chalk.white("Avg Cost"),
              chalk.white("Avg Sessions"),
              chalk.white("Avg Days"),
            ],
            colAligns: ["left", "center", "right", "right", "center", "right"],
          });

          for (const b of benchmarks) {
            benchTable.push([
              `${b.complexity}-${b.category}`,
              b.dataPoints.toString(),
              b.avgTokens.toLocaleString(),
              `$${b.avgCost.toFixed(2)}`,
              b.avgSessions.toFixed(1),
              b.avgWallClockDays.toFixed(1),
            ]);
          }

          console.log(benchTable.toString());
        }
      }

      // Sync to Convex
      const now = new Date();
      const period = `${now.getFullYear()}-W${String(Math.ceil((now.getTime() - new Date(now.getFullYear(), 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000))).padStart(2, "0")}`;

      const benchmarks = generateBenchmarks(projectVelocity.features);

      const synced = await syncVelocity({
        projectName,
        period,
        featuresCompleted: projectVelocity.featuresCompleted,
        totalTokens: projectVelocity.totalTokens,
        totalCost: projectVelocity.totalCost,
        avgEstimateAccuracy: projectVelocity.overallAccuracy,
        avgCostPerFeature: projectVelocity.avgCostPerFeature,
        avgDaysPerFeature: projectVelocity.avgWallClockDays,
        cacheHitRate: 0,
        autoTagRate: 0,
        benchmarks: benchmarks.map((b) => ({
          category: b.category,
          complexity: b.complexity,
          avgTokens: b.avgTokens,
          avgCost: b.avgCost,
          avgSessions: b.avgSessions,
          avgWallClockDays: b.avgWallClockDays,
          avgActiveMinutes: b.avgActiveHours * 60,
          p25Days: b.sessionRange?.p25 ?? b.avgWallClockDays * 0.7,
          p75Days: b.sessionRange?.p75 ?? b.avgWallClockDays * 1.3,
          dataPoints: b.dataPoints,
          confidence: Math.min(b.dataPoints / 10, 1),
        })),
      });
      logConvexStatus(synced);
    },
  );
